import React, { RefObject, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useDragElement } from '../hooks/useDragElement';
import IndividualChapterItem from './ChapterItem';
import AddChapterButton from './AddChapterButton';
import { getPreviousSegmentAbsoluteTime, MIN_SEGMENT_WIDTH } from '../timeUtils';
import { zoomLevelStore } from '../store/zoomLevel';
import { TIMELINE_COMPONENT_CLASS_IDENTIFIER } from '../constants';
import { getClipFPS, getEndForSegment, getStartForSegment } from '@/player-config/src/utils/clipUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { isSplitsInTimelineEnabled } from '@/stores/clip';

// !! TODO @HiteshChoudhary0098 make this component independent of composite segment
export default function TimelineChapter({
  compositeSegmentDragStart,
  compositeSegmentDragEnd,
  newTimelineContainerRef,
  speakerTimelineContainerRef,
  chaptersContainerRef,
  compositeSegment
}: {
  compositeSegmentDragEnd: number;
  compositeSegmentDragStart: number;
  newTimelineContainerRef: RefObject<HTMLDivElement>;
  speakerTimelineContainerRef: RefObject<HTMLDivElement>;
  chaptersContainerRef: RefObject<HTMLDivElement>;
  compositeSegment: CompositeSegment;
}) {
  const [draggingChapter, setDraggingChapter] = useState<string | null>(null);
  const [draggingChapterOffset, setDraggingChapterOffset] = useState<number>(0);
  const transcriptStore = useTranscriptContext();
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const { updateChapter } = useTranscriptPage();
  const { clipData, fps } = useClipsContext();

  const [isDraggingTimelineEntity, setIsDraggingTimelineEntity] = useState(false);

  const isSplitsEnabled = useMemo(() => {
    return isSplitsInTimelineEnabled(clipData);
  }, [clipData]);

  const chaptersWithEndTime = useMemo(() => {
    let maxVideoDuration = clipData.asset_metadata.maxVideoDuration;
    if (isSplitsEnabled) {
      const playerConfig = clipData.asset_metadata.player_config;
      maxVideoDuration = playerConfig?.durationInFrames ? playerConfig.durationInFrames : maxVideoDuration;

      if (!chapters || !maxVideoDuration) {
        return [];
      }
      const fps = getClipFPS(clipData);

      const chapterAdjustedWithDrag = chapters.map(chapter => ({
        ...chapter,
        start_time: chapter.start_time + (chapter.id === draggingChapter ? draggingChapterOffset : 0)
      }));

      const chapterWithEndTime = chapterAdjustedWithDrag.map((chapter, index) => ({
        ...chapter,
        end_time: chapterAdjustedWithDrag[index + 1]?.start_time || (maxVideoDuration || 0) / fps
      }));
      const chapterWithTrimCorrection = chapterWithEndTime.map(chapter => ({
        ...chapter,
        start_time: Math.max(0, chapter.start_time),
        end_time: Math.max(chapter.end_time, 0)
      }));

      return chapterWithTrimCorrection;
    }
    if (compositeSegment) {
      maxVideoDuration = compositeSegment.maxBounds.toFrame - compositeSegment.maxBounds.fromFrame;
    }

    if (!compositeSegment || !chapters || !maxVideoDuration) {
      return [];
    }

    const compositeSegmentStartTime = getStartForSegment({
      clip: clipData,
      segmentId: compositeSegment.id,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
    const compositeSegmentEndTime = getEndForSegment({
      clip: clipData,
      segmentId: compositeSegment.id,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });

    const compositeStartOffsetTime = compositeSegmentStartTime + compositeSegmentDragStart;
    const compositeEndOffsetTime = compositeSegmentEndTime + compositeSegmentDragEnd;

    const chapterAdjustedWithDrag = chapters.map(chapter => ({
      ...chapter,
      start_time:
        chapter.start_time - compositeStartOffsetTime + (chapter.id === draggingChapter ? draggingChapterOffset : 0)
    }));

    const chapterWithEndTime = chapterAdjustedWithDrag.map((chapter, index) => ({
      ...chapter,
      end_time:
        chapterAdjustedWithDrag[index + 1]?.start_time ||
        maxVideoDuration - compositeStartOffsetTime + compositeEndOffsetTime ||
        0
    }));
    const chapterWithTrimCorrection = chapterWithEndTime.map(chapter => ({
      ...chapter,
      start_time: Math.max(0, chapter.start_time),
      end_time: Math.min(compositeEndOffsetTime - compositeStartOffsetTime, Math.max(chapter.end_time, 0))
    }));

    const visibleChapters = chapterWithTrimCorrection.filter(
      chapter => chapter.start_time < compositeEndOffsetTime - compositeStartOffsetTime
    );
    return visibleChapters;
  }, [
    chapters,
    clipData,
    compositeSegment,
    compositeSegmentDragEnd,
    compositeSegmentDragStart,
    draggingChapter,
    draggingChapterOffset
  ]);

  const onChapterDragEnd = useCallback(
    (offset: number, chapterId: string) => {
      if (!chapters || !chapterId) {
        return;
      }
      const chapter = chapters.find(chapter => chapter.id === chapterId);
      if (!chapter) {
        return;
      }
      const newStartTime = chapter.start_time + offset;
      updateChapter(chapterId, undefined, newStartTime);
    },
    [chapters, updateChapter]
  );

  const { onMouseDown } = useDragElement({
    setDraggingElement: setDraggingChapter,
    setDraggingElementOffset: setDraggingChapterOffset,
    onDragEnd: onChapterDragEnd,
    validDropZoneRef: newTimelineContainerRef,
    droppableContainerRef: speakerTimelineContainerRef,
    fps
  });

  useEffect(() => {
    const handleNewTimelineEntityDraggingStateChanged = (data: { isDraggingTimelineEntity: boolean }) => {
      setIsDraggingTimelineEntity(data.isDraggingTimelineEntity);
    };
    EventBus.on(CustomEvents.NewTimelineEntityDraggingStateChanged, handleNewTimelineEntityDraggingStateChanged);
    return () => {
      EventBus.off(CustomEvents.NewTimelineEntityDraggingStateChanged, handleNewTimelineEntityDraggingStateChanged);
    };
  }, []);

  const getChapterStartAndEndTime = useCallback(
    (id: string) => {
      const chapterIndex = chapters?.findIndex(chapter => chapter.id === id);
      if (chapterIndex === undefined || chapterIndex === -1 || !chapters || !compositeSegment) {
        return {
          start_time: 0,
          end_time: 0
        };
      }
      return {
        start_time: chapters[chapterIndex].start_time,
        end_time: chapters[chapterIndex + 1]?.start_time || compositeSegment.currentBounds.toFrame / fps
      };
    },
    [chapters, compositeSegment, fps]
  );
  const { zoomLevel } = useSyncExternalStore(zoomLevelStore.subscribe, zoomLevelStore.getSnapshot);

  const previousSegmentAbsoluteTime = useMemo(() => {
    if (!compositeSegment || isSplitsEnabled) {
      return 0;
    }
    return getPreviousSegmentAbsoluteTime(compositeSegment, clipData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compositeSegment, clipData, zoomLevel]);

  const chaptersStartTimeEndTime = useMemo(() => {
    return chaptersWithEndTime.map(chapter => getChapterStartAndEndTime(chapter.id));
  }, [chaptersWithEndTime, getChapterStartAndEndTime]);

  const clipStartTime = useMemo(() => {
    return clipData.asset_metadata.start;
  }, [clipData.asset_metadata.start]);

  return (
    <div
      ref={chaptersContainerRef}
      className={TIMELINE_COMPONENT_CLASS_IDENTIFIER.CHAPTER_CONTAINER}
      style={
        previousSegmentAbsoluteTime > 0
          ? {
              paddingLeft: `max(calc(${previousSegmentAbsoluteTime * fps}px * var(--timeline-zoom)),${MIN_SEGMENT_WIDTH}px)`
            }
          : {}
      }
    >
      <div className="group/timeline-chapter relative flex h-[30px]">
        {!isDraggingTimelineEntity && <AddChapterButton />}
        {chaptersWithEndTime?.map((chapter, index) => (
          <IndividualChapterItem
            key={chapter.id}
            chapter={chapter}
            clipStartTime={clipStartTime}
            chapterStartAndEndTime={chaptersStartTimeEndTime[index]}
            onMouseDown={onMouseDown}
            previousChapter={chaptersWithEndTime[index - 1]}
            nextChapter={chaptersWithEndTime[index + 1]}
            isDraggingChapter={draggingChapter === chapter.id}
            isDragging={isDraggingTimelineEntity}
          />
        ))}
      </div>
    </div>
  );
}
